/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { Avatar } from "../../shared/Avatar";
import { shiftDate } from "../../shared/dates";
import type { Member, RotaSettings } from "../../Team/types";
import { warningsFor, type Absence, type PlannedShift, type RotaWarning } from "../engine/rules";
import { warningText } from "../Schedule/warningText";

type Request = {
  id: number;
  kind: "claim" | "offer";
  status: "pending" | "approved" | "declined" | "cancelled";
  note: string | null;
  staff_id: number;
  staff_name: string;
  staff_color: string | null;
  shift_id: number;
  shift_date: string;
  start_time: string;
  end_time: string;
  break_minutes: number;
  role_id: number | null;
  role_name: string | null;
  shift_staff_id: number | null;
};

const day = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

// Claims of open shifts and offers to give a shift away. For a claim, any
// labour rule the claimant would break shows before approving.
export const ShiftRequests = defineComponent({
  name: "ShiftRequests",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const list = ref<Request[]>([]);
    const loaded = ref(false);
    const warnings = ref(new Map<number, RotaWarning[]>());

    const load = async () => {
      const [reqs, team, settings] = await Promise.all([trpc.rota.requests.list.query(), trpc.rota.team.list.query(), trpc.rota.settings.get.query()]);
      list.value = reqs as Request[];
      loaded.value = true;
      const claims = list.value.filter((r) => r.kind === "claim" && r.status === "pending");
      if (!claims.length) return (warnings.value = new Map());
      const dates = claims.map((c) => c.shift_date).sort();
      const around = { from: shiftDate(dates[0], -7), to: shiftDate(dates[dates.length - 1], 7) };
      const [shifts, off] = await Promise.all([trpc.rota.shifts.list.query(around), trpc.rota.timeOff.list.query(around)]);
      const absences = (off as (Absence & { status: string })[]).filter((o) => o.status === "approved");
      const members = team.members as Member[];
      const map = new Map<number, RotaWarning[]>();
      for (const c of claims) {
        const person = members.find((m) => m.id === c.staff_id);
        if (!person) continue;
        const shift: PlannedShift = { id: c.shift_id, shift_date: c.shift_date, start_time: c.start_time, end_time: c.end_time, break_minutes: c.break_minutes, role_id: c.role_id, staff_id: c.staff_id };
        map.set(c.id, warningsFor(shift, person, shifts as PlannedShift[], settings as RotaSettings, absences));
      }
      warnings.value = map;
    };
    onMounted(load);

    const decide = async (r: Request, approve: boolean) => {
      // Show the decision at once; the reload confirms it.
      list.value = list.value.map((x) => (x.id === r.id ? { ...x, status: approve ? "approved" : "declined" } : x));
      try {
        await trpc.rota.requests.decide.mutate({ id: r.id, approve });
        await load();
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
        await load();
      }
    };

    return () => (
      <div class="rounded-2xl border border-base-300 bg-base-100 p-4">
        <div class="mb-3 font-semibold">{t("workday.requests.shift_requests")}</div>
        {!loaded.value ? (
          <div class="flex justify-center py-6">
            <Loading />
          </div>
        ) : list.value.length === 0 ? (
          <p class="py-6 text-center text-sm text-base-content/50">{t("workday.requests.no_shift_requests")}</p>
        ) : (
          <ul class="divide-y divide-base-200">
            {list.value.map((r) => {
              const w = warnings.value.get(r.id) ?? [];
              return (
                <li key={r.id} class="flex flex-wrap items-center gap-3 py-2.5">
                  <Avatar name={r.staff_name} id={r.staff_id} color={r.staff_color} size="sm" />
                  <div class="min-w-0 flex-1">
                    <div class="text-sm">
                      <span class="font-medium">{r.staff_name}</span>{" "}
                      <span class="text-base-content/60">{t(`workday.requests.kind_${r.kind}`)}</span>
                    </div>
                    <div class="text-xs text-base-content/60">
                      {`${day(r.shift_date)}, ${r.start_time} – ${r.end_time}`}
                      {r.role_name && ` · ${r.role_name}`}
                      {r.note && ` · “${r.note}”`}
                    </div>
                    {w.length > 0 && <div class="text-xs text-warning">{w.map((x) => warningText(x, t)).join(" · ")}</div>}
                  </div>
                  {r.status === "pending" ? (
                    <div class="flex gap-1">
                      <button class="btn btn-ghost btn-sm" onClick={() => decide(r, false)}>
                        {t("workday.requests.decline")}
                      </button>
                      <button class="btn btn-success btn-sm" onClick={() => decide(r, true)}>
                        {t("workday.requests.approve")}
                      </button>
                    </div>
                  ) : (
                    <span class={["badge badge-sm", r.status === "approved" ? "badge-success" : "badge-error badge-outline"].join(" ")}>
                      {t(`teamlink.page.status_${r.status}`)}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  },
});
